import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  PhoneIncoming, CheckCircle2, AlertTriangle, XCircle, RefreshCw,
  Phone, Radio, Wifi, ArrowRight, ShieldAlert, Clock, Settings2, PhoneOutgoing,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Link } from 'react-router-dom';
import { voiceAPI } from '../../lib/api';

const PROVIDER_META = {
  twilio: { name: 'Twilio', icon: Phone, blurb: 'Global inbound via Twilio + ElevenLabs (TwiML stream).' },
  exotel: { name: 'Exotel', icon: Radio, blurb: 'India inbound via your ExoPhone Voicebot applet streaming to ElevenLabs.' },
  voicelink: { name: 'VoiceLink', icon: Wifi, blurb: 'India SIP inbound routed through your own ElevenLabs trunk.' },
};

const VERDICT = {
  ready: { label: 'Ready', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  collision: { label: 'Phone conflict', color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert },
  needs_setup: { label: 'Needs setup', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  needs_provision: { label: 'Needs provisioning', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Settings2 },
  disabled: { label: 'Disabled', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: XCircle },
  not_configured: { label: 'Not connected', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: XCircle },
  coming_soon: { label: 'Coming soon', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Clock },
};

function ProviderCard({ p, onProvision, provisioning, onConnectVoicelink, onEnableOutbound, vlBusy }) {
  const meta = PROVIDER_META[p.provider] || { name: p.provider, icon: Phone, blurb: '' };
  const verdict = VERDICT[p.verdict] || VERDICT.not_configured;
  const Icon = meta.icon;
  const VIcon = verdict.icon;
  const canProvision = p.provider === 'exotel' && ['needs_provision', 'ready', 'collision'].includes(p.verdict) && p.enabled && p.phone_number;
  const isVoicelink = p.provider === 'voicelink';

  return (
    <Card data-testid={`inbound-provider-${p.provider}`} className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-900 p-2.5 text-white"><Icon className="h-5 w-5" /></div>
            <div>
              <CardTitle className="text-lg">{meta.name}</CardTitle>
              <CardDescription className="text-sm mt-0.5">{meta.blurb}</CardDescription>
            </div>
          </div>
          <Badge className={`shrink-0 border ${verdict.color}`} data-testid={`inbound-verdict-${p.provider}`}>
            <VIcon className="h-3.5 w-3.5 mr-1" />{verdict.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {p.configured && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Detail label="Number" value={p.phone_number || '—'} />
            <Detail label="AI phone registered" ok={p.has_elevenlabs_phone} />
            {p.provider === 'exotel' && <Detail label="Applet id" value={p.has_applet_id ? 'set' : '—'} ok={p.has_applet_id} />}
            {isVoicelink && <Detail label="Outbound (AI dials)" ok={!!p.outbound_enabled} />}
            {p.inbound_calls && <Detail label="Inbound calls (all-time)" value={String(p.inbound_calls.total)} />}
          </div>
        )}

        {(p.issues || []).map((msg, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 p-2.5 text-sm text-red-700" data-testid={`inbound-issue-${p.provider}-${i}`}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{msg}</span>
          </div>
        ))}
        {(p.next_steps || []).map((msg, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-100 p-2.5 text-sm text-slate-600">
            <ArrowRight className="h-4 w-4 mt-0.5 shrink-0" /><span>{msg}</span>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {isVoicelink ? (
            <>
              <Button size="sm" variant={p.configured ? 'outline' : 'default'} onClick={onConnectVoicelink} disabled={vlBusy} data-testid="voicelink-connect-btn">
                <Wifi className="h-4 w-4 mr-1.5" />{p.configured ? 'Reconnect' : 'Connect VoiceLink'}
              </Button>
              {p.verdict === 'ready' && !p.outbound_enabled && (
                <Button size="sm" onClick={onEnableOutbound} disabled={vlBusy} data-testid="voicelink-enable-outbound-btn">
                  <PhoneOutgoing className="h-4 w-4 mr-1.5" />Enable outbound
                </Button>
              )}
            </>
          ) : (
            <Link to="/integrations">
              <Button variant="outline" size="sm" data-testid={`inbound-configure-${p.provider}`}>
                <Settings2 className="h-4 w-4 mr-1.5" />Configure
              </Button>
            </Link>
          )}
          {canProvision && (
            <Button size="sm" onClick={() => onProvision()} disabled={provisioning} data-testid="inbound-provision-btn">
              {provisioning ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <PhoneIncoming className="h-4 w-4 mr-1.5" />}
              Provision Inbound AI
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Detail({ label, value, ok }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      {typeof ok === 'boolean' ? (
        <div className={`font-medium flex items-center gap-1 ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
          {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {value || (ok ? 'Yes' : 'No')}
        </div>
      ) : (
        <div className="font-medium text-slate-700 truncate">{value}</div>
      )}
    </div>
  );
}

export default function InboundVoiceHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [vlDialog, setVlDialog] = useState(false);
  const [vlUser, setVlUser] = useState('');
  const [vlPass, setVlPass] = useState('');
  const [vlBusy, setVlBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await voiceAPI.getInboundHealth();
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load inbound voice health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    setProvisioning(true);
    try {
      const res = await voiceAPI.provisionExotelInbound();
      if (res.data?.provisioned) {
        toast.success('Inbound AI provisioned — your ExoPhone now answers with the AI agent.');
      } else {
        toast.error(res.data?.hint || res.data?.reason || 'Provisioning could not complete.');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Provisioning failed');
    } finally {
      setProvisioning(false);
    }
  };

  const connectVoicelink = async () => {
    if (!vlUser || !vlPass) { toast.error('Enter your VoiceLink username and password'); return; }
    setVlBusy(true);
    try {
      const res = await voiceAPI.connectVoicelink(vlUser, vlPass);
      toast.success(`VoiceLink connected — DID ${res.data?.did} routed to your AI (KYC: ${res.data?.kyc || 'ok'}).`);
      setVlDialog(false);
      setVlUser(''); setVlPass('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'VoiceLink connection failed');
    } finally {
      setVlBusy(false);
    }
  };

  const enableOutbound = async () => {
    setVlBusy(true);
    try {
      await voiceAPI.enableVoicelinkOutbound();
      toast.success('Outbound enabled — the AI can now place calls over your VoiceLink SIP trunk.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not enable outbound');
    } finally {
      setVlBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="inbound-hub-loading">
        <Skeleton className="h-9 w-72" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  const ready = data?.inbound_ready;

  return (
    <div className="space-y-6" data-testid="inbound-voice-hub">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <PhoneIncoming className="h-7 w-7 text-indigo-600" />Inbound Voice Hub
          </h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            One place to set up and health-check how incoming calls reach your AI agent —
            across Twilio, Exotel and VoiceLink (all using your own ElevenLabs voice).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            className={`border ${ready ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}
            data-testid="inbound-overall-status"
          >
            {ready ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <AlertTriangle className="h-3.5 w-3.5 mr-1" />}
            {ready ? 'Inbound AI is live' : 'Inbound AI not fully set up'}
          </Badge>
          <Button variant="outline" size="sm" onClick={load} data-testid="inbound-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {(data?.providers || []).map((p) => (
          <ProviderCard
            key={p.provider} p={p} onProvision={provision} provisioning={provisioning}
            onConnectVoicelink={() => setVlDialog(true)} onEnableOutbound={enableOutbound} vlBusy={vlBusy}
          />
        ))}
      </div>

      <Dialog open={vlDialog} onOpenChange={setVlDialog}>
        <DialogContent data-testid="voicelink-connect-dialog">
          <DialogHeader>
            <DialogTitle>Connect VoiceLink</DialogTitle>
            <DialogDescription>
              Sign in with your VoiceLink reseller/client credentials. We'll find your DID + ElevenLabs SIP trunk,
              register the number with your AI agent, and route inbound calls automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="vl-user">VoiceLink username</Label>
              <Input id="vl-user" value={vlUser} onChange={(e) => setVlUser(e.target.value)} placeholder="e.g. LOCKENE" data-testid="voicelink-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vl-pass">Password</Label>
              <Input id="vl-pass" type="password" value={vlPass} onChange={(e) => setVlPass(e.target.value)} data-testid="voicelink-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVlDialog(false)} disabled={vlBusy}>Cancel</Button>
            <Button onClick={connectVoicelink} disabled={vlBusy} data-testid="voicelink-connect-submit">
              {vlBusy ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Wifi className="h-4 w-4 mr-1.5" />}Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-slate-50 border-slate-100">
        <CardContent className="pt-5 text-sm text-slate-600 space-y-1.5">
          <p className="font-medium text-slate-700">How inbound routing works</p>
          <p>• A caller dials your connected number → your carrier's Voicebot/SIP flow streams the audio to ElevenLabs → the AI agent we assigned to that number answers, personalised to the matched lead.</p>
          <p>• <b>Phone conflict</b> means the same ElevenLabs phone id is shared with another account. Contact support (governor) to re-provision an exclusive phone.</p>
          <p>• For Exotel, confirm your applet actually streams to ElevenLabs before expecting calls to connect.</p>
        </CardContent>
      </Card>
    </div>
  );
}
